import React from "react";
import PropTypes from "prop-types";
import { Form, Row, Col } from "antd";
import {
  CustomCheckbox,
  InputTextAreaField,
  RadioButton,
} from "../../../../../../CommonComponents/commonStyles";
import {
  RecognitionLegend,
  FormContainer,
  SubSectionLabel,
  RadioWrapper,
  LastNameWrapper,
  ErrorMsgWrap,
  CommentsMargin,
  TableLabel,
  WrapLabel,
  InputMargin,
  LabelWrapper,
  InputWrapper,
  LastInputMargin,
  GrantInputRadio,
  CheckboxMargin,
  CheckboxLabel,
  InputSection,
  AccountWordWrap,
  RecognitionFieldset,
  LabelSection
} from "../../style";
import TooltipComponent from "../../../../../../CommonComponents/Info/Tooltip/TooltipComponent";
import FormBase from "../../../../../../CommonComponents/FormBase/FormBase";
import regexMap from "../../../../../../utils/regexMap";
import { getPrimaryAccount } from "../../../../../../utils/sessionUtil";
import { tooltipAligner } from "../../utils";

class GrantRecognitionForm extends FormBase {
  constructor(props) {
    super(props);
    this.state = {
      recognitionOption: {
        value: props.recognitionOption || "10",
        valid: "",
        touched: false,
        validationRules: {
          isRequired: true,
        },
        messages: {
          isRequired: "Please select an option to continue.",
        },
      },
      advisorThirdPartyComment: {
        value: props.comments || "",
        valid: "",
        touched: false,
      },
      includeAdvisorInfo: {
        value: props.includeAdvisorInfo || false,
        valid: "",
        touched: false,
      },
      memoryOf: {
        value: props.memoryOf || "",
        valid: "",
        touched: false,
      },
      honourOf: {
        value: props.honorOf || "",
        valid: "",
        touched: false,
      },
      firstName: {
        value: props.firstName || "",
        touched: false,
        valid: "",
        matchPattern: regexMap.name,
        validationRules: {
          dependentOn: "lastName",
        },
        messages: {
          dependentOn: "Please specify first name to continue.",
        },
      },
      lastName: {
        value: props.lastName || "",
        touched: false,
        valid: "",
        matchPattern: regexMap.name,
        validationRules: {
          dependentOn: "firstName",
          minLength: 2,
        },
        messages: {
          dependentOn: "Please specify last name to continue.",
          minLength: "Last Name is too short. Please correct to proceed",
        },
      },
      accountName: getPrimaryAccount().accountName,
    };
    this.handleBlur = this.handleBlur.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    this.checkFormValid = this.checkFormValid.bind(this);
    this.runFormValidation = this.runFormValidation.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.individualValidator !== this.props.individualValidator) {
      this.runFormValidation();
    }
  }

  handleRecognitionChange = (event) => {
    this.setState({
      advisorThirdPartyComment: {
        value: "",
        valid: "",
        touched: false,
        validationRules: {
          isRequired: event.target.value === "20",
        },
        messages: {
          isRequired: "Please enter third party information",
        },
      },
      includeAdvisorInfo: {
        value: false,
        valid: "",
        touched: false,
      },
    });
    this.handleInputChange(event);
  };

  formatData = () => ({
    data: {
      grantRecognition: this.state.recognitionOption.value,
      notifyFirstName: this.state.firstName.value,
      notifyLastName: this.state.lastName.value,
      inMemoryOfName: this.state.memoryOf.value,
      inHonorOfName: this.state.honourOf.value,
      thirdParty: this.state.advisorThirdPartyComment.value,
      includeAdvisorInfo: !this.state.includeAdvisorInfo.value,
      accountName: this.state.accountName,
    },
    isFormValid: this.checkFormValid(this.state),
  });

  render() {
    const {
      recognitionOption,
      advisorThirdPartyComment,
      memoryOf,
      honourOf,
      firstName,
      lastName,
      includeAdvisorInfo,
      accountName,
    } = this.state;
    return (
      <FormContainer>
        <RecognitionFieldset>
          <RecognitionLegend id="recognition-box">Grant Recognition</RecognitionLegend>
          <TableLabel>
            {this.props.recognitionText
              ? this.props.recognitionText
              : "Indicate who you would like the charity to recognize for this grant."}
          </TableLabel>
          <RadioWrapper className="mt-20" aria-labelledby="recognition-box">
            <Form.Item>
              <GrantInputRadio>
                <RadioButton
                  name="recognitionOption"
                  value="10"
                  onChange={this.handleRecognitionChange}
                  checked={recognitionOption.value === "10"}
                  className="mr1"
                  aria-labelledby="recognition-box recog1-detail"
                />
                <LabelSection id="recog1-detail">
                  Account advisor name(s), address(es), and philanthropic
                  account name
                  <TooltipComponent
                    id="advisor"
                    placement={tooltipAligner("#advisor")}
                    text="Account advisor are the individuals with account privileges and access, excluding interested parties."
                  />
                </LabelSection>
              </GrantInputRadio>
              <GrantInputRadio>
                <RadioButton
                  name="recognitionOption"
                  value="20"
                  onChange={this.handleRecognitionChange}
                  checked={recognitionOption.value === "20"}
                  className="mr1"
                  aria-labelledby="recognition-box recog2-detail"
                />
                <LabelSection id="recog2-detail">
                  Account advisor name(s), address(es), and philanthropic
                  account name, and a third party (individual or organization).
                  <TooltipComponent
                    id="thirdParty"
                    placement={tooltipAligner("#thirdParty")}
                    text="Names and contact information for another party you specify here. 
                  Account advisor names and contact information will also be displayed in a CC section at the bottom of the letter.
                  Select the checkbox if you do not wish for the names and contact information of the account advisors to be included."
                  />
                </LabelSection>
              </GrantInputRadio>
              <Row>
                <Col lg={{ offset: 1 }} md={{ offset: 1 }} sm={{ offset: 1 }}>
                  <CommentsMargin>
                    <Form.Item>
                      <InputTextAreaField
                        id="txtAdvisorThirdPartyComment"
                        name="advisorThirdPartyComment"
                        aria-label="third-party-comment"
                        rows={4}
                        maxLength={300}
                        onBlur={this.handleBlur}
                        onChange={this.handleInputChange}
                        disabled={recognitionOption.value !== "20"}
                        value={advisorThirdPartyComment.value}
                      />
                      {advisorThirdPartyComment.valid && (
                        <ErrorMsgWrap>
                          {
                            advisorThirdPartyComment.messages[
                              advisorThirdPartyComment.valid
                            ]
                          }
                        </ErrorMsgWrap>
                      )}
                    </Form.Item>
                  </CommentsMargin>
                  <CheckboxMargin>
                    <CustomCheckbox
                      name="includeAdvisorInfo"
                      onChange={this.handleCheckboxChange}
                      checked={includeAdvisorInfo.value}
                      disabled={recognitionOption.value !== "20"}
                      aria-labelledby="include-advisor"
                    ></CustomCheckbox>
                    <CheckboxLabel id="include-advisor">
                      Do not include account advisor information on the letter
                      accompanying the check sent to the grantee organization.
                    </CheckboxLabel>
                  </CheckboxMargin>
                </Col>
              </Row>
              <GrantInputRadio>
                <RadioButton
                  name="recognitionOption"
                  value="40"
                  onChange={this.handleRecognitionChange}
                  checked={recognitionOption.value === "40"}
                  className="mr1"
                  aria-labelledby="recognition-box recog3-detail"
                />
                <LabelSection isWordBreak id="recog3-detail">
                  Account name only:{" "}
                  <AccountWordWrap>{accountName}</AccountWordWrap>
                  <TooltipComponent
                    id="accountName"
                    placement={tooltipAligner("#accountName")}
                    text="Name of your philanthropic account without any contact information."
                  />
                </LabelSection>
              </GrantInputRadio>
              <GrantInputRadio>
                <RadioButton
                  name="recognitionOption"
                  value="30"
                  onChange={this.handleRecognitionChange}
                  checked={recognitionOption.value === "30"}
                  className="mr1"
                  aria-labelledby="recognition-box recog4-detail"
                />
                <LabelSection id="recog4-detail">
                  Anonymous
                  <TooltipComponent
                    id="anonymous"
                    placement={tooltipAligner("#anonymous")}
                    text="No identifying or contact information."
                  />
                </LabelSection>
              </GrantInputRadio>
            </Form.Item>
          </RadioWrapper>
        </RecognitionFieldset>
        <Row>
          <SubSectionLabel boldLetter margin="30px 0">
            Additional attribution:
          </SubSectionLabel>
          <Col lg={8}>
            <WrapLabel>
              <InputMargin>
                <Form.Item
                  colon={false}
                  label={<LabelWrapper for="txtmemoryOf">In memory of (optional)</LabelWrapper>}
                >
                  <InputWrapper>
                    <InputSection
                      id="txtmemoryOf"
                      name="memoryOf"
                      aria-label="memoryOf"
                      role="textbox"
                      placeholder="In memory of"
                      maxLength={150}
                      onChange={this.handleInputChange}
                      value={memoryOf.value}
                      onBlur={this.handleBlur}
                    />
                  </InputWrapper>
                </Form.Item>
              </InputMargin>
            </WrapLabel>
          </Col>
        </Row>
        <Row>
          <Col lg={8}>
            <WrapLabel>
              <InputMargin>
                <Form.Item
                  colon={false}
                  label={<LabelWrapper for="txthonorOf">In honor of (optional)</LabelWrapper>}
                >
                  <InputWrapper>
                    <InputSection
                      id="txthonorOf"
                      name="honourOf"
                      aria-label="honorOf"
                      placeholder="In honor of"
                      maxLength={150}
                      onChange={this.handleInputChange}
                      value={honourOf.value}
                      onBlur={this.handleBlur}
                    />
                  </InputWrapper>
                </Form.Item>
              </InputMargin>
            </WrapLabel>
          </Col>
        </Row>
        <WrapLabel>
          <LastInputMargin>
            <LabelWrapper>Please notify (optional)</LabelWrapper>
            <TooltipComponent
              id="specify"
              placement={tooltipAligner("#specify")}
              text="Enter the name of a person at the grantee organization who should be notified of the grant's arrival."
            />
            <Row className="mt1">
              <Col xl={8} lg={8} md={24}>
                <Form.Item>
                  <InputWrapper>
                    <InputSection
                      id="txtNotifyFirstName"
                      name="firstName"
                      placeholder="First name"
                      aria-label="first-name"
                      role="textbox"
                      maxLength={150}
                      onChange={this.handleInputChange}
                      value={firstName.value}
                      onBlur={this.handleBlur}
                    />
                    {firstName.valid && (
                      <ErrorMsgWrap>
                        {firstName.messages[firstName.valid]}
                      </ErrorMsgWrap>
                    )}
                  </InputWrapper>
                </Form.Item>
              </Col>
              <Col xl={8} lg={{ span: 9, offset: 1 }} md={24}>
                <Form.Item>
                  <LastNameWrapper>
                    <InputWrapper>
                      <InputSection
                        id="txtNotifylastName"
                        name="lastName"
                        placeholder="Last name"
                        aria-label="last-name"
                        role="textbox"
                        onChange={this.handleInputChange}
                        maxLength={150}
                        minLength={2}
                        value={lastName.value}
                        onBlur={this.handleBlur}
                      />
                      {lastName.valid && (
                        <ErrorMsgWrap>
                          {lastName.messages[lastName.valid]}
                        </ErrorMsgWrap>
                      )}
                    </InputWrapper>
                  </LastNameWrapper>
                </Form.Item>
              </Col>
              <Col lg={6} md={24} className="pl1 mt1">
                <LabelWrapper>of this grant</LabelWrapper>
              </Col>
            </Row>
          </LastInputMargin>
        </WrapLabel>
      </FormContainer>
    );
  }
}

GrantRecognitionForm.propTypes = {
  comments: PropTypes.string,
  firstName: PropTypes.string,
  handleFormChange: PropTypes.func,
  honorOf: PropTypes.string,
  includeAdvisorInfo: PropTypes.bool,
  individualValidator: PropTypes.bool,
  lastName: PropTypes.string,
  memoryOf: PropTypes.string,
  recognitionOption: PropTypes.string,
};

export default GrantRecognitionForm;
